import { ReactElement, useState } from 'react'

import { Button } from 'antd'

import { Drawer } from 'components/core'
import { PermissionTooltip } from 'components/PermissionTooltip/PermissionTooltip'
import { ToolTip } from 'components/ToolTip'
import { userHasManageAmsPermissions } from 'helpers/featurePermissions'
import { useUserHasPermissions } from 'hooks'
import {
    AdGroupGroupBy,
    CampaignGroupBy,
    KeywordGroupBy,
    QueryFact,
    ResolvedExpressionGroupBy,
} from 'types'

import {
    KeywordDrawerContent,
    NegativeKeywordDrawerContent,
} from './DrawerContent'

interface Props<RecordType> {
    record: RecordType
}

type BaseRecordType = QueryFact<
    CampaignGroupBy &
        AdGroupGroupBy &
        KeywordGroupBy &
        ResolvedExpressionGroupBy
>

function isSupported<RecordType extends BaseRecordType>(
    record: RecordType
): boolean {
    return (
        !!record.keyword ||
        ['loose-match', 'close-match'].includes(record.resolved_expression)
    )
}

const addKeywordLabel = '+ Add Keyword'
const addNegativeKeywordLabel = '+ Add Negative'

function AddKeywordCell<RecordType extends BaseRecordType>({
    record,
}: Props<RecordType>): ReactElement {
    const [isKeywordDrawerOpen, setKeywordDrawerOpen] = useState(false)
    const [isNegativeKeywordDrawerOpen, setNegativeKeywordDrawerOpen] =
        useState(false)

    const canManageAms = useUserHasPermissions(userHasManageAmsPermissions)

    if (!canManageAms) {
        return (
            <PermissionTooltip hasPermission={false}>
                <>
                    <Button disabled style={{ marginRight: 8 }}>
                        {addKeywordLabel}
                    </Button>
                    <Button disabled>{addNegativeKeywordLabel}</Button>
                </>
            </PermissionTooltip>
        )
    }

    if (!isSupported(record)) {
        const toolTipTitle = 'Only available for Keyword Search Terms'
        return (
            <>
                <ToolTip placement="top" title={toolTipTitle}>
                    <Button disabled style={{ marginRight: 8 }}>
                        {addKeywordLabel}
                    </Button>
                </ToolTip>
                <ToolTip placement="top" title={toolTipTitle}>
                    <Button disabled>{addNegativeKeywordLabel}</Button>
                </ToolTip>
            </>
        )
    }

    const selectedSearchTerm = {
        id: record.query,
        record,
    }

    const toggleKeywordDrawer = (): void => {
        setKeywordDrawerOpen((open) => !open)
    }

    const toggleNegativeKeywordDrawer = (): void => {
        setNegativeKeywordDrawerOpen((open) => !open)
    }

    return (
        <>
            <Drawer
                title="Add Search Term as Keyword"
                closable
                onClose={toggleKeywordDrawer}
                open={isKeywordDrawerOpen}
                width={700}
            >
                <KeywordDrawerContent
                    selectedSearchTerm={selectedSearchTerm}
                    closeDrawer={toggleKeywordDrawer}
                />
            </Drawer>
            <Drawer
                title="Add Search Term as Negative Keyword"
                closable
                onClose={toggleNegativeKeywordDrawer}
                open={isNegativeKeywordDrawerOpen}
                width={700}
            >
                <NegativeKeywordDrawerContent
                    selectedSearchTerm={selectedSearchTerm}
                    closeDrawer={toggleNegativeKeywordDrawer}
                />
            </Drawer>
            <Button onClick={toggleKeywordDrawer} style={{ marginRight: 8 }}>
                {addKeywordLabel}
            </Button>
            <Button onClick={toggleNegativeKeywordDrawer}>
                {addNegativeKeywordLabel}
            </Button>
        </>
    )
}

export default AddKeywordCell
